import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";

const emojis = [
    "🐶", "🚗", "🦄", "🎈", "🚀", "🐘", "🍎", "🏀", "📕", "⚽",
    "🍕", "🎸", "🚢", "🐱", "🌞", "🍔", "🚲", "🏕", "📡", "🔑",
    "🐳", "🌵", "🎠", "🍩", "🧀", "🚂", "🐙", "🦆", "🏖", "📖",
    "🍉", "🌊", "🎻", "🌈", "🍟", "⚡", "🎯", "🐟", "🚜", "🦅",
    "📱", "📚", "🥕", "🦖", "🎃", "🐬", "🎁", "🍇", "🚀", "🛶",
    "🦜", "🏹", "🐧", "🦊", "🏰", "🚦", "🎠", "🌼", "💎", "🗻",
    "🦄", "📌", "🥦", "🎷", "🔬", "🐼", "📎", "🦕", "🍵", "⚔️",
    "🦉", "🌙", "🎭", "🏜", "📀", "🦢", "🐎", "🧊", "🎇", "🏄",
    "🚁", "🦋", "🦔", "🍪", "🛰", "🐝", "🎤", "🦑", "🏎", "🍍"
];

const BigOrSmallGame: React.FC = () => {
    const [emoji, setEmoji] = useState<string>("🍎");
    const [questionType, setQuestionType] = useState<string>("big"); // "big" or "small"
    const [bigOnLeft, setBigOnLeft] = useState<boolean>(true);
    const [correctAnswer, setCorrectAnswer] = useState<string>("");
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Big and Small Worksheets for UKG | Size Comparison Activities";
    }, []);

    const generateNewQuestion = () => {
        // Choose a random emoji
        const selectedEmoji = emojis[Math.floor(Math.random() * emojis.length)];
        setEmoji(selectedEmoji);

        // Randomly decide if the left or right emoji is bigger
        const isLeftBig = Math.random() > 0.5;
        setBigOnLeft(isLeftBig);

        // Randomly decide if the question asks for "big" or "small"
        const isBigQuestion = Math.random() > 0.5;
        setQuestionType(isBigQuestion ? "big" : "small");

        // Determine the correct answer
        const correct = isBigQuestion ? (isLeftBig ? "left" : "right") : (isLeftBig ? "right" : "left");
        setCorrectAnswer(correct);

        setMessage("");
    };

    const checkAnswer = (selected: string) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach UKG students the concept of big and small with engaging worksheets, comparisons, and picture-based activities. Download free size comparison worksheets now!" />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/pre-math-concepts/big-small" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Big and Small</h1>

                                    <p>
                                        Understanding size differences is one of the first steps in early math development. This <strong>big and small</strong> concept helps UKG learners compare and sort objects based on their size. It also builds a strong foundation for logical thinking and visual discrimination.
                                    </p>

                                    <h2>🧠 Why Learning Big and Small Is Important</h2>
                                    <p>
                                        Recognizing the difference between big and small helps children:
                                        <ul>
                                            <li>Classify objects by size</li>
                                            <li>Develop observation and comparison skills</li>
                                            <li>Enhance early math vocabulary</li>
                                            <li>Prepare for <strong>sorting, ordering, and measurements in higher grades</strong></li>
                                        </ul>
                                    </p>

                                    <h2>📚 What Will Your Child Learn?</h2>
                                    <ul>
                                        <li>Identify big and small objects in pictures</li>
                                        <li>Compare objects and select the bigger or smaller one</li>
                                        <li>Match pairs of big and small items</li>
                                        <li>Circle or color objects based on their size</li>
                                    </ul>

                                    <h2>🖍️ Activities in the Worksheets</h2>
                                    <ul>
                                        <li>Tick the bigger object</li>
                                        <li>Match small and big objects from a group</li>
                                        <li>Circle the smallest item in a row</li>
                                        <li>Draw and color your own <strong>big and small items</strong></li>
                                    </ul>

                                    <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                    <ul>
                                        <li>Use real objects <strong>like toys, fruits, and blocks to compare sizes</strong></li>
                                        <li>Ask your child to find something <strong>“bigger” or “smaller”</strong> around the house</li>
                                        <li>Play sorting games with household items based on size</li>
                                        <li>Use visual storytelling or picture books to reinforce the concept</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Long - Short</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Heavy - Light</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Front - Back</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Play sorting games with your child using toys or clothes. Ask, <strong>“Can you find the biggest teddy?” or “Which sock is smaller?”</strong> — simple activities go a long way!
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Big and Small</h1>

                                <p>
                                    Understanding size differences is one of the first steps in early math development. This <strong>big and small</strong> concept helps UKG learners compare and sort objects based on their size. It also builds a strong foundation for logical thinking and visual discrimination.
                                </p>

                                <h2>🧠 Why Learning Big and Small Is Important</h2>
                                <p>
                                    Recognizing the difference between big and small helps children:
                                    <ul>
                                        <li>Classify objects by size</li>
                                        <li>Develop observation and comparison skills</li>
                                        <li>Enhance early math vocabulary</li>
                                        <li>Prepare for <strong>sorting, ordering, and measurements in higher grades</strong></li>
                                    </ul>
                                </p>

                                <h2>📚 What Will Your Child Learn?</h2>
                                <ul>
                                    <li>Identify big and small objects in pictures</li>
                                    <li>Compare objects and select the bigger or smaller one</li>
                                    <li>Match pairs of big and small items</li>
                                    <li>Circle or color objects based on their size</li>
                                </ul>

                                <h2>🖍️ Activities in the Worksheets</h2>
                                <ul>
                                    <li>Tick the bigger object</li>
                                    <li>Match small and big objects from a group</li>
                                    <li>Circle the smallest item in a row</li>
                                    <li>Draw and color your own big and small items</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                <ul>
                                    <li>Use real objects like toys, fruits, and blocks to compare sizes</li>
                                    <li>Ask your child to find something <strong>“bigger” or “smaller”</strong> around the house</li>
                                    <li>Play sorting games with household items based on size</li>
                                    <li>Use visual storytelling or picture books to reinforce the concept</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Heavy - Light</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Front - Back</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Play sorting games with your child using toys or clothes. Ask, <strong>“Can you find the biggest teddy?” or “Which sock is smaller?”</strong> — simple activities go a long way!
                                </p>
                            </article>
                            <h2>Which one is {questionType}?</h2>

                            <div style={{ display: "flex", justifyContent: "center", gap: "50px", margin: "20px 0" }}>
                                <div
                                    onClick={() => checkAnswer("left")}
                                    style={{ cursor: "pointer", fontSize: bigOnLeft ? "80px" : "40px" }}
                                >
                                    {emoji}
                                </div>
                                <div
                                    onClick={() => checkAnswer("right")}
                                    style={{ cursor: "pointer", fontSize: bigOnLeft ? "40px" : "80px" }}
                                >
                                    {emoji}
                                </div>
                            </div>

                            <p style={{ fontSize: "20px" }}>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default BigOrSmallGame;
